import React from 'react'

import { useTheme } from '@mui/material/styles'

import type { BarDataProps } from '../../../../types/Charts/BarChart'

interface BarStyleProps extends React.CSSProperties {
  x?: number
  y?: number
  width?: number
  height?: number
}

export interface CustomBarPlotV2Props {
  colorRange: string[]
  data: BarDataProps[]
  dataIndex: number
  removeSelectedTabColorChange?: boolean
  selectedTabIndex: number
  style: BarStyleProps
  // Non-DOM props forwarded by the x-charts BarPlot `bar` slot; declared so we
  // can destructure them out before spreading restProps onto <path>.
  xOrigin?: number
  yOrigin?: number
  skipAnimation?: boolean
}

const createRoundedRectPath = (w: number, h: number) => {
  if (w <= 0 || h <= 0) return ''

  const r = Math.min(4, w / 2, h / 2)
  return `M${r},0 L${w - r},0 Q${w},0 ${w},${r} L${w},${h} L0,${h} L0,${r} Q0,0 ${r},0 Z`
}

export const CustomBarPlotV2 = ({
  colorRange,
  data,
  dataIndex,
  removeSelectedTabColorChange,
  selectedTabIndex,
  style,
  // Non-DOM props forwarded by the @mui/x-charts BarPlot `bar` slot. They are
  // not valid SVG attributes, so pull them out of restProps before spreading
  // onto <path> to avoid React "does not recognize the prop" warnings.
  xOrigin: _xOrigin,
  yOrigin: _yOrigin,
  skipAnimation: _skipAnimation,
  ...restProps
}: CustomBarPlotV2Props) => {
  const theme = useTheme()

  const { ariaLabel, formattedAmount } = data[dataIndex]

  const shouldAmountAmountTextBeBold = selectedTabIndex === dataIndex ? 'bold' : 400

  const x = style.x ?? 0
  const y = style.y ?? 0
  const width = style.width ?? 0
  const height = style.height ?? 0

  return (
    <g aria-label={ariaLabel}>
      <path
        {...restProps}
        d={createRoundedRectPath(width, height)}
        fill={colorRange[dataIndex]}
        transform={`translate(${x}, ${y})`}
      />
      <text
        fill={theme.palette.text.primary}
        fontSize={11}
        fontWeight={removeSelectedTabColorChange ? 'bold' : shouldAmountAmountTextBeBold}
        textAnchor="middle"
        x={x + width / 2}
        y={y - 8}
      >
        {formattedAmount}
      </text>
    </g>
  )
}
